/**
 * LEAD-360-FOUNDATION-01 — PHI-free Lead 360 presentation view-model (contract).
 *
 * The pure, deterministic foundation for a Salesforce-style "Lead 360" record in
 * AdoraCare. Per LEAD-360-FOUNDATION-SCOUT-01 this is the SAFE FIRST STEP: it
 * defines the stable, PHI-free shape a future backend read-model must satisfy and
 * projects it for display — WITHOUT reading the `leads` table, querying the DB, or
 * rendering any UI. (A live leads-read endpoint and the UI are deferred to
 * separately-reviewed packages because the raw `leads` row is PHI-bearing and
 * real lead inflow is still dark.)
 *
 * SAFETY BY CONSTRUCTION
 *   - PURE + DETERMINISTIC: same input → same output. No React, no fetch, no
 *     Supabase, no API/backend import, no env, no `Date.now()`, no `Math.random()`,
 *     no mutation of the input.
 *   - PHI-BOUNDED (CONTACT-REVEAL-AUDIT-01 carve): this is a SCOPED-OPERATOR
 *     contract — it carries the lead's name in the single `leadName` field
 *     (ratified: name renders plain to scoped operators on the lead list and
 *     Lead 360 detail) plus a no-digit `phoneAvailability` status, and otherwise
 *     ONLY safe fields (opaque lead id, clinic id + clinic LABEL, source/UTM,
 *     timestamps, lifecycle statuses, provenance, role visibility). It still
 *     declares NO phone value / email / age / gender / fingerprint / contact id /
 *     recording / transcript, and this module never adds, derives, or echoes any
 *     such field. The raw `leads` contact-channel columns (`primary_phone_e164`,
 *     `primary_phone_fingerprint`) are NOT part of this contract; the plaintext
 *     phone is reachable ONLY through the separate audited tap-to-reveal
 *     endpoint, never through this view-model.
 *   - HONEST BY DEFAULT: missing UTM/source → "Not captured"; dark hops (call,
 *     revenue) → explicit `missingHops`, never faked; a probable/unknown walk-in
 *     is reported as probable/partial, never as a confirmed link; an empty event
 *     list DEFAULTS to `eventStreamStatus: 'not_wired'` ("no wired reader produced
 *     this record"), NEVER "no activity happened" — UNLESS a wired read-model
 *     explicitly passes `eventStreamStatus: 'available'`, in which case a
 *     wired-but-empty stream stays honestly "available" (connected, no events
 *     yet). Unknown is never converted into false certainty.
 *
 * EVENT VOCABULARY
 *   `Lead360EventType` mirrors the FULL `lead_events.event_type` CHECK
 *   vocabulary -- the backend `LeadEventType` (lib/domain/lead/types.ts) and
 *   its frozen runtime list `LEAD_EVENT_TYPES` (lib/domain/lead/
 *   lead-events.ts). The enum is declared LOCALLY (no backend import) so the
 *   frontend bundle stays decoupled; the root equivalence pin
 *   tests/unit/lead-360-event-vocabulary-equivalence.test.ts holds this
 *   mirror in LOCKSTEP with the backend vocabulary (a drifted member fails
 *   the build -- the OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 corrective-1
 *   guard). A wired backend read-model now supplies real `events` plus an
 *   explicit `eventStreamStatus`; legacy/demo producers may omit both, which
 *   reads as an empty, `'not_wired'` stream.
 */

// ─── Vocabulary (local, structural mirrors of future backend types) ──

/**
 * Full `lead_events.event_type` CHECK vocabulary -- the structural mirror of
 * the backend `LeadEventType` (lib/domain/lead/types.ts) and its frozen
 * runtime list `LEAD_EVENT_TYPES` (lib/domain/lead/lead-events.ts). The type
 * is DERIVED from this list, so the type and the list cannot drift apart;
 * the cross-bundle equivalence pin
 * (tests/unit/lead-360-event-vocabulary-equivalence.test.ts) proves the list
 * member-and-order equal to the backend's. Do not invent new values here --
 * extend the backend vocabulary first (migration + types + list), then
 * mirror.
 */
const LEAD360_EVENT_TYPE_LIST = [
  // original 8 (migration 20260513_2)
  'lead_created',
  'called',
  'appointment_booked',
  'visited',
  'not_answered',
  'closed',
  'missed_call',
  'follow_up_needed',
  // contact dispositions (migration 20260613_1)
  'wrong_number',
  'call_later',
  'interested',
  'not_interested',
  'just_checking',
  // forward stage hops (migration 20260613_1)
  'qualified',
  'not_booked',
  'arrived',
  'checked_in',
  'no_show',
  'cancelled',
  'lost',
  // positive-conversion-gate stages (ADR-0018; migration 20260629_1)
  'patient',
  'converted_to_revenue',
  // lead record-ownership events (LEAD-ASSIGNMENT-OWNERSHIP-V1; migration
  // 20260704_1) -- WHO owns the lead changed; never a stage move.
  'lead_claimed',
  'lead_assigned',
  'lead_reassigned',
  'lead_released',
  // pre-visit closure dispositions (PREVISIT-SLA-DISPOSITIONS-V1; migration
  // 20260717_2, FILE-ONLY)
  'not_reachable',
  'duplicate',
  // LEAD-UNTIL-CONSULT ruling (DOCTOR-OS-EXPERIENCE-V1; migration
  // 20260804_1) -- registration captured onto the lead; a journey event,
  // never a mint marker.
  'registration_completed',
] as const;

export type Lead360EventType = (typeof LEAD360_EVENT_TYPE_LIST)[number];

/** Frozen runtime vocabulary (the equivalence guard's comparison surface). */
export const LEAD360_EVENT_TYPES: readonly Lead360EventType[] =
  Object.freeze(LEAD360_EVENT_TYPE_LIST);

/**
 * READ-TIME-ONLY view members synthesized by the backend read model and
 * never persisted to `lead_events` (each is provenance-marked on the wire).
 * Same order as the backend union; full per-member provenance docs live at
 * the backend home (lib/domain/lead/lead-360.ts).
 */
const LEAD360_VIEW_ONLY_EVENT_TYPE_LIST = [
  // a linked telephony call whose outcome could not be classified (a call
  // happened; answered vs missed is not guessed)
  'call_logged',
  // appointment lifecycle entries projected from `appointment_events`
  // (S2-P2D / S2-P3); 'lead_not_converted' carries a controlled
  // `outcomeReason`
  'appointment_confirmed',
  'appointment_rescheduled',
  'appointment_doctor_changed',
  'appointment_treatment_changed',
  'appointment_cancelled',
  'appointment_no_show',
  'patient_arrived',
  'patient_checked_in',
  'appointment_fulfilled',
  'lead_not_converted',
  // consultation-outcome entries projected from `appointment_events`
  // (BACK-HALF PIECE 1); 'treatment_declined' may carry a controlled
  // `outcomeReason`
  'treatment_advised',
  'treatment_accepted',
  'treatment_declined',
  'lead_converted',
  // consult-started view member (FRONTDESK-CHECKIN-AND-IDENTITY-V1, rider
  // R-07) -- an appointment_events-sourced view token
  'consult_started',
  // a walk-in Activity entry projected from `walkins` via the explicit
  // walkins.lead_id link (S2-P5A4); summary carries only the humanized,
  // controlled walk-in source token
  'walkin_registered',
  // contact-revealed view member (CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-
  // CORE-V1) - a read-time Activity entry from audit_log CONTACT_REVEAL
  // rows; PHI-free (purpose token + role only, never the value)
  'contact_revealed',
  // note-added view member (OMNICHANNEL-TIMELINE-AND-LEAD-360-V1) - a
  // read-time Activity entry from the dedicated lead_activities store,
  // merged into the single-lead timeline read. NEVER persisted to
  // lead_events. A voided note still renders (noteVoided) - struck, never
  // hidden (the truth law)
  'note_added',
] as const;

/**
 * Lead 360 VIEW event vocabulary -- structural mirror of the backend
 * `Lead360ViewEventType` (lib/domain/lead/lead-360.ts,
 * LEAD-360-CALL-LINKAGE-READ-01): the DB-locked enum above extended with the
 * READ-TIME-ONLY view members. The DB enum itself is unchanged -- do NOT add
 * members there.
 */
export type Lead360ViewEventType =
  | Lead360EventType
  | (typeof LEAD360_VIEW_ONLY_EVENT_TYPE_LIST)[number];

/** Frozen runtime view-only vocabulary (the guard's comparison surface). */
export const LEAD360_VIEW_ONLY_EVENT_TYPES: readonly Exclude<
  Lead360ViewEventType,
  Lead360EventType
>[] = Object.freeze(LEAD360_VIEW_ONLY_EVENT_TYPE_LIST);

/**
 * Canonical lifecycle STAGE token (LIFECYCLE-RENDER-V1 / V1-P2) - the structural
 * mirror of the backend engine `LeadStage`
 * (lib/domain/lead/lead-lifecycle.ts:39-59). Local copy by the established
 * separate-packages convention (same posture as `Lead360AppointmentStatus`
 * below); kept in LOCKSTEP with the engine by the root equivalence pin
 * (tests/unit/lead-lifecycle-rail-view-equivalence.test.ts), which fails the
 * build if this order/vocabulary drifts from `LEAD_STAGES`. This is the CANON
 * forward stage axis - DISTINCT from `Lead360LifecycleCompleteness` below, which
 * is LINKAGE/identity completeness, not a lifecycle stage. Do not conflate them:
 * conflating linkage-completeness with the stage was the V1-P2 founder-evidenced
 * mixup ("Source attributed" rendered as a lifecycle stage).
 */
export type Lead360Stage =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'appointment_booked'
  | 'arrived'
  | 'patient'
  | 'converted_to_revenue'
  | 'lost';

/** Honest data-mode flag for a lead record (extends the leakage 'real'|'demo'). */
export type Lead360DataMode = 'real' | 'demo' | 'test' | 'manual' | 'unknown';

/**
 * Lifecycle-hop status. Deliberately NOT a boolean so uncertainty cannot be
 * flattened into false certainty: a fingerprint walk-in match is `probable`,
 * a dark stream is `not_available`, and an unobserved hop is `unknown`.
 */
export type Lead360HopStatus =
  | 'yes'
  | 'no'
  | 'probable'
  | 'unknown'
  | 'not_available';

/**
 * Phone-availability status (CONTACT-REVEAL-AUDIT-01) — the structural mirror
 * of the backend `Lead360PhoneAvailability` (lib/domain/lead/lead-360.ts). A
 * STATUS, never a boolean and never a value: every member is a digit-free
 * token. 'no_linked_phone' is a DESIGNED outcome ("No linked phone yet"),
 * never an error; 'unknown' means the presence lookup could not run.
 */
export type Lead360PhoneAvailability = 'available' | 'no_linked_phone' | 'unknown';

/**
 * Management roles eligible for lead visibility in v0. Per the scout, runtime
 * scope is role-bucketed (lib/scope-filter.ts `UserScope`); per-user identity
 * (IAM `staff_users`) is NOT surfaced yet, so v0 stays role-level only. Doctor /
 * receptionist / patient are intentionally excluded — lead governance is a
 * management concern, and per-user / incentive design is explicitly out of scope.
 */
export type Lead360Role = 'owner' | 'gm_agm' | 'regional_head' | 'coc';

/**
 * The work-routing role a live lead_actions row may be assigned to - the
 * structural mirror of the backend `LeadActionOwnerRole` (lib/domain/lead/
 * lead-360.ts). WIDER than `Lead360Role` on purpose: the live next-action owner
 * can be the front desk ('receptionist') or the blended-team call center
 * ('call_center_agent'). Drives the DISPLAY-only `openActionOwner` label
 * below; never authorization.
 *
 * LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (Refs #315): `call_center_agent` added so
 * the stored lead owner renders truthfully; display capability only (the
 * server strips this field for every tier but leadership).
 */
export type LeadActionOwnerRole =
  | 'receptionist'
  | 'coc'
  | 'regional_head'
  | 'gm_agm'
  | 'owner'
  | 'call_center_agent';

/**
 * Presentation SECTIONS of a Lead 360 record — the structural mirror of the
 * backend `Lead360Section` (lib/domain/lead/lead-360.ts,
 * S1-P4-ROLE-AWARE-LEAD360-VIEWS-01). Local copy by the established
 * separate-packages convention; kept in lockstep with the backend tier-section
 * map (lib/domain/lead/lead-360-role-view.ts) by review. Used ONLY to render
 * the sections a role-aware payload actually carries — never an access grant.
 */
export type Lead360Section =
  | 'identity'
  | 'contact'
  | 'highlights'
  | 'nextActionOwner'
  | 'source'
  | 'journey'
  | 'activity'
  | 'appointment'
  | 'linkage';

/**
 * The PHI-free record-OWNERSHIP view attached to a Lead 360 record
 * (LEAD-ASSIGNMENT-OWNERSHIP-V1, V2-P0) -- the structural mirror of the backend
 * `LeadOwnershipView` (lib/domain/lead/lead-assignment.ts), kept in LOCKSTEP by
 * review. WHO is accountable for this lead right now -- a NEW plane, DISTINCT
 * from `openActionOwner` (the next-action owner ROLE) and `assignedRole` (the
 * responsible-role default). Carries NO raw staff id: `isMine` drives the
 * My-leads filter, `ownerLabel` is a controlled OPERATOR display label (staff,
 * never a patient value), `assigned=false` is the POOL badge, `escalated` is the
 * unclaimed-over-threshold badge (only meaningful when `assigned=false`).
 */
export interface LeadOwnershipView {
  readonly assigned: boolean;
  readonly isMine: boolean;
  readonly ownerLabel: string | null;
  readonly escalated: boolean;
}

/**
 * Canonical (FHIR current-state) appointment status — the structural mirror of
 * the backend `AppointmentStatus` (lib/domain/appointment/appointment-status.ts).
 * Local copy by the established separate-packages convention. `null` on a card
 * means the stored status did not project onto this view (never guessed).
 */
export type Lead360AppointmentStatus =
  | 'waitlist'
  | 'booked'
  | 'arrived'
  | 'checked_in'
  | 'fulfilled'
  | 'cancelled'
  | 'no_show';

/**
 * Controlled appointment-action event type — structural mirror of the backend
 * `AppointmentEventType` (lib/domain/appointment/appointment-events.ts). S2-P3
 * added the four check-in / outcome events to the original four; this mirror
 * catches up with the since-shipped tokens (booking-confirm 20260706_1, the
 * back-half outcome four 20260629_1) and adds consult_started
 * (FRONTDESK-CHECKIN-AND-IDENTITY-V1; migration 20260711_1).
 */
export type Lead360AppointmentEventType =
  | 'appointment_booked'
  | 'appointment_rescheduled'
  | 'appointment_doctor_changed'
  | 'appointment_treatment_changed'
  | 'appointment_cancelled'
  | 'appointment_no_show'
  | 'appointment_confirmed'
  | 'patient_arrived'
  | 'patient_checked_in'
  | 'appointment_fulfilled'
  | 'lead_not_converted'
  | 'treatment_advised'
  | 'treatment_accepted'
  | 'treatment_declined'
  | 'lead_converted'
  | 'consult_started';

/** One rendered history entry — mirror of backend `AppointmentEventView`.
 * PHI-free: an honest role-derived `actorLabel` (never a fabricated name) +
 * timestamps; never the raw actor principal. */
export interface Lead360AppointmentEventView {
  readonly eventType: Lead360AppointmentEventType;
  readonly occurredAt: string;
  readonly actorLabel: string;
  readonly actorRole: string;
  readonly summary: string;
  readonly previousAppointmentTime: string | null;
  readonly newAppointmentTime: string | null;
  /**
   * Controlled outcome reason (S2-P3) - present only on a lead_not_converted
   * entry; null otherwise. A controlled token, never a free-text note.
   * OPTIONAL on the mirror so a payload from an older backend stays valid.
   */
  readonly outcomeReason?: string | null;
  /**
   * Controlled cancel-reason code (rider R-08) - present only on an
   * appointment_cancelled entry; null otherwise (and absent pre-20260711_2).
   * An org-config token; the label rides the cancel-reasons label module.
   */
  readonly cancelReasonCode?: string | null;
}

/** "Who did it / when" — mirror of backend `AppointmentActorStamp`. */
export interface Lead360AppointmentActorStamp {
  readonly label: string;
  readonly role: string;
  readonly at: string;
}

/** Per-appointment history — mirror of backend `AppointmentHistoryView`. */
export interface Lead360AppointmentHistoryView {
  readonly bookedBy: Lead360AppointmentActorStamp | null;
  readonly lastChangedBy: Lead360AppointmentActorStamp | null;
  readonly events: readonly Lead360AppointmentEventView[];
}

/**
 * One administrative appointment card — the structural mirror of the backend
 * `LeadAppointmentCardView` (lib/domain/appointment/appointment-intake.ts).
 * PHI-SAFE BY CONSTRUCTION: it carries NO patient name / phone / clinical note /
 * doctor identity — only the administrative date/time, the canonical + stored
 * status, a doctor-assigned BOOLEAN, the optional category, and (S2-P2C) the
 * actor-stamped history (role labels + timestamps only).
 */
/** One payment evidence row on an appointment card (V1-P6 corrective). */
export interface Lead360PaymentEvidenceRow {
  readonly paymentId: number;
  readonly occurredAtIso: string | null;
  /** Minor units (paise) exactly as stored; display divides by 100. */
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly method: string | null;
  readonly active: boolean;
  readonly voidedAtIso: string | null;
  readonly voidReason?: string | null;
  readonly recordedByLabel?: string | null;
  /** WHO voided this row (PAYMENT-WHO-VOIDED-TO-WIRE-V1) -- the structural
   *  mirror of the backend AppointmentPaymentEvidenceRow.voidedByLabel.
   *  Supervisor-tier (ABSENT on the front_desk wire); null on an active row. */
  readonly voidedByLabel?: string | null;
}

export interface Lead360AppointmentCardView {
  readonly appointmentId: number;
  readonly appointmentTime: string;
  readonly status: Lead360AppointmentStatus | null;
  readonly storedStatus: string;
  readonly doctorAssigned: boolean;
  readonly category: string | null;
  /**
   * S2-P2C workbench history; null when none recorded (honest empty). OPTIONAL
   * on the mirror so a payload from an older backend (mid-rollout, before the
   * field shipped) stays valid — the UI renders history only when present.
   */
  readonly history?: Lead360AppointmentHistoryView | null;
  /**
   * RECORD-360-WORKSPACE-V1 (V1-P5): per-appointment OPERATIONAL visit + money
   * facts -- the structural mirror of the backend AppointmentVisitMoneyFacts
   * spread (lib/domain/appointment/appointment-intake.ts). The SAME
   * administrative signal set the clinic-day board wire already ships
   * (visit existence/status, plan-status COUNTS, ONE whole-INR accepted sum,
   * payment evidence) -- never a procedure name, category token, or clinical
   * text. ALL OPTIONAL: absent on payloads from older backends / legacy
   * fixtures / a fail-safe degrade; consumers render honest unknown states
   * (the current-truth contract maps absence to its explicit
   * 'unknown_on_this_read' states, never a fabricated value).
   */
  readonly visitStatus?: 'none' | 'draft' | 'finalized';
  readonly caseSheetId?: number | null;
  readonly acceptedPlanCount?: number;
  readonly advisedPlanCount?: number;
  readonly suggestedCollectAmount?: number | null;
  readonly paymentRecorded?: boolean;
  /** V1-P6 corrective: this episode's payment EVIDENCE ROWS (voided rows
   *  included, marked) -- the structural mirror of the backend
   *  AppointmentPaymentEvidenceRow. Absent when the enrichment did not run.
   *  Supervisor fields (voidReason / recordedByLabel / voidedByLabel) are
   *  tier-shaped server-side: ABSENT on the front_desk wire. */
  readonly payments?: readonly Lead360PaymentEvidenceRow[];
}

/**
 * The Lead 360 appointment section — the structural mirror of the backend
 * `LeadAppointmentSectionView`. Honest empty-state (`emptyStateLabel`) when the
 * lead has no appointments; never a fixture/sample card.
 */
export interface Lead360AppointmentSectionView {
  readonly hasAppointments: boolean;
  readonly cards: readonly Lead360AppointmentCardView[];
  readonly emptyStateLabel: string | null;
}

/** Where a record/figure came from, plus an honest caveat. PHI-free. */
export interface Lead360Provenance {
  /** e.g. 'adoracare_lp', 'vendor_sync', 'demo_fixture'. Never PHI. */
  readonly system: string;
  /** Plain-English honesty caveat, carried through verbatim. */
  readonly caveat: string;
}

/**
 * A single lead lifecycle event — PHI-free. Carries either a persisted
 * `lead_events` row (sanitized `summary` = `payload_summary`, NEVER raw vendor
 * payload) or a READ-TIME synthesized view event (the lead_created origin / a
 * linked telephony call), distinguished by `provenance` — the structural mirror
 * of the backend `Lead360Event` (lib/domain/lead/lead-360.ts). Real rows are
 * supplied by the wired read-model and writer; view events are never persisted.
 */
export interface Lead360Event {
  readonly eventType: Lead360ViewEventType;
  /** ISO timestamp string (no Date object — keeps this module pure/portable). */
  readonly occurredAt: string;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  /** Sanitized, human-readable, PHI-free summary line. */
  readonly summary: string;
  readonly provenance: string;
  /**
   * OPTIONAL reschedule old/new appointment times (S2-P2D) — present ONLY on a
   * read-time 'appointment_rescheduled' Activity entry so the renderer can show
   * the IST change ("from X -> to Y"). ISO timestamp strings; never a phone /
   * name / clinical value. OMITTED on every other event (backward-compatible).
   */
  readonly previousAppointmentTime?: string | null;
  readonly newAppointmentTime?: string | null;
  /**
   * OPTIONAL controlled outcome reason (S2-P3) - present ONLY on a read-time
   * 'lead_not_converted' Activity entry so the renderer can show the reason
   * ("Lead not converted (price) by ..."). A controlled token, never a
   * free-text note, never PHI. OMITTED on every other event.
   */
  readonly outcomeReason?: string | null;
  /**
   * OPTIONAL typed disposition facts (CALL-CENTER-AGENT-SCOPE-AND-
   * DISPOSITION-CORE-V1) - the controlled tokens parsed server-side from a
   * persisted disposition lead_events row, so the timeline + the
   * last-attempt summary render TYPED facts from the SAME wire. All
   * controlled slugs / an ISO instant; never free text, never PHI. OMITTED
   * on every non-disposition event (backward-compatible). `revealPurpose`
   * rides the read-time 'contact_revealed' entries only.
   */
  readonly dispositionOutcomeId?: string | null;
  readonly dispositionConnectId?: string | null;
  readonly dispositionDetailId?: string | null;
  readonly dispositionNextActionId?: string | null;
  readonly dispositionCallbackAtIso?: string | null;
  readonly dispositionChannel?: string | null;
  readonly revealPurpose?: string | null;
  /**
   * OPTIONAL note payload (OMNICHANNEL-TIMELINE-AND-LEAD-360-V1) - present
   * ONLY on a read-time 'note_added' entry from the single-lead timeline
   * read (the org-fenced, role-gated wire; never a list wire). `noteText`
   * is the bounded, digit-run-guarded PHI-LIGHT operator note; `noteVoided`
   * marks the void-supersede state (rendered struck, never hidden).
   * OMITTED on every other event (additive; existing fixtures unchanged).
   */
  readonly noteText?: string | null;
  readonly noteVoided?: boolean;
  readonly noteActivityId?: number | null;
}

/**
 * Input contract — the structural mirror of a FUTURE PHI-safe backend lead
 * read-model. SAFE FIELDS ONLY. (No patient name/phone/email/age/gender/
 * fingerprint/contactId — those are never part of this contract.)
 */
export interface Lead360Input {
  /** Opaque, owner-scoped lead reference (the leads surrogate id, not PHI). */
  readonly safeLeadId: string;
  readonly clinicId: number | null;
  /** Resolved clinic name/label (non-PHI administrative metadata) or null. */
  readonly clinicLabel: string | null;
  readonly sourceNormalized: string | null;
  readonly sourceRaw?: string | null;
  readonly utmSource?: string | null;
  readonly utmMedium?: string | null;
  readonly utmCampaign?: string | null;
  readonly utmTerm?: string | null;
  readonly utmContent?: string | null;
  /**
   * LIFECYCLE-RENDER-V1 (V1-P2): the committed canonical lifecycle STAGE token
   * (leads.enquiry_status verbatim, validated backend-side through the engine
   * guard). The stage rail (`lead360.stage.rail`) and the next-action card
   * (`lead360.nextaction.card`) render from THIS - never from the linkage-hop
   * completeness below. `null` when the stored value is not a canon stage or the
   * producer did not wire it (legacy/demo/fixtures); consumers then show the
   * honest "stage not recorded" state, never a fabricated stage.
   */
  readonly stage?: Lead360Stage | null;
  /**
   * Presence-only paid-click indicator — the structural mirror of the backend
   * `Lead360Input.paidClickPresent` (lib/domain/lead/lead-360.ts). True when the
   * representative attribution row carried ANY paid-click id (gclid / gbraid /
   * wbraid / fbclid / ctwa_clid). NEVER the raw value — a boolean only. Optional
   * for legacy/demo producers; consumers treat absence as `false`.
   */
  readonly paidClickPresent?: boolean;
  /** ISO timestamp string, or null when not captured. */
  readonly createdAt: string | null;
  readonly dataMode: Lead360DataMode;
  readonly provenance: Lead360Provenance;

  // ── Scoped-operator identity + contact presence (CONTACT-REVEAL-AUDIT-01) ──
  /**
   * The lead's name, rendered PLAIN to scoped operators (decision 3). Null
   * when not captured — never fabricated. Optional so legacy/demo producers
   * predating the carve stay valid; the wired read-model always emits it.
   */
  readonly leadName?: string | null;
  /**
   * No-digit availability status (never a phone value, never digits).
   * Optional for legacy producers; consumers treat absence as 'unknown'.
   */
  readonly phoneAvailability?: Lead360PhoneAvailability;

  // ── Lifecycle hop statuses (status, not boolean — see Lead360HopStatus) ──
  readonly leadCaptured: Lead360HopStatus;
  readonly sourceCaptured: Lead360HopStatus;
  readonly callLinked: Lead360HopStatus;
  readonly walkinLinked: Lead360HopStatus;
  readonly revenueLinked: Lead360HopStatus;

  /**
   * Safe events only, populated by the wired read-model. An empty array paired
   * with `eventStreamStatus: 'available'` means wired-but-no-events (honest); an
   * OMITTED `events` means no reader produced this record.
   */
  readonly events?: readonly Lead360Event[];
  /**
   * Wired-ness of the event stream for this record — mirrors the backend
   * `Lead360Input.eventStreamStatus` (lib/domain/lead/lead-360.ts). The
   * read-model sets `'available'` once it queries `lead_events`, EVEN WHEN the
   * result is empty, so a zero-event lead never reads as `'not_wired'`. OMITTED
   * by legacy / demo / non-read-model producers; consumers then fall back to
   * `events.length > 0`. (Forward-references `Lead360EventStreamStatus`, declared
   * with the other presentation-output types below — legal for type aliases.)
   */
  readonly eventStreamStatus?: Lead360EventStreamStatus;

  /**
   * OPTIONAL administrative appointment section (S2-P2) — the structural mirror
   * of the backend `Lead360Input.appointmentSection`. Built from REAL
   * `appointments` rows for this lead; PHI-safe (date/time, status,
   * doctor-assigned, category — never a name / phone / clinical field).
   * OMITTED by legacy / demo producers and existing fixtures (consumers render
   * nothing for the section); the wired read-model always emits it. Visible to
   * EVERY operator tier including front_desk (appointments are operational data
   * the front desk manages) — the deliberate opposite of the source restriction.
   */
  readonly appointmentSection?: Lead360AppointmentSectionView;

  /**
   * OPTIONAL golden-record link state -- the structural mirror of the
   * backend `Lead360Input.goldenRecordLink` (PATIENT-MASTER-CONVERSION-
   * AND-PAYMENT-LINK-COHERENCE-V1). Drives the golden-record card's
   * on-load state (linked / possible match / none) + the returning
   * "Existing patient" chip. OMITTED by legacy producers / failed
   * enrichment -- consumers keep the action-driven card and claim
   * nothing. PHI posture: the opaque record UUID + the UMR token ONLY --
   * no name on this wire (leadName stays the only name-bearing key),
   * never a phone.
   */
  readonly goldenRecordLink?: {
    readonly status: 'linked' | 'possible_match' | 'none';
    readonly recordId?: string;
    readonly umr?: string;
    readonly candidateCount?: number;
  };

  /**
   * OPTIONAL returning-patient RECOGNITION -- the structural mirror of the
   * backend `Lead360Input.returningVisitor`
   * (RETURNING-PATIENT-RECOGNITION-AND-LEAD-LINKAGE-V1, Refs #315). The
   * lead-time "this phone already belongs to a patient" signal, rendered as
   * an ATTRIBUTED OBSERVATION (badge + fenced facts), never a golden-record
   * write.
   *   - { tier: 'confident', recordId, recordName, knownSince, lastVisitDate,
   *       homeClinicLabel }  EXACTLY ONE active same-org match -> the fenced
   *       IDENTITY + RELATIONSHIP facts (name, patient-since, last-visit DATE,
   *       home clinic ONLY; ZERO clinical content).
   *   - { tier: 'ambiguous', candidateCount }  MORE THAN ONE match (a family
   *       sharing a phone) -> COUNT ONLY, no names, no auto-pick.
   * OMITTED for the 'none' tier, an already-linked lead, or a failed lookup.
   * Display-only; the confident badge is EVIDENCE of a returning patient, not
   * absolute identity proof. Consumed straight off the raw record (like
   * goldenRecordLink) -- never echoed through buildLead360View's PHI-scanned
   * output.
   */
  readonly returningVisitor?:
    | {
        readonly tier: 'confident';
        readonly recordId: string;
        readonly recordName: string;
        readonly knownSince: string | null;
        readonly lastVisitDate: string | null;
        readonly homeClinicLabel: string | null;
      }
    | { readonly tier: 'ambiguous'; readonly candidateCount: number };

  // ── Role-level visibility (v0: roles only, never per-user) ──
  readonly assignedRole?: Lead360Role | null;
  readonly visibleRoles?: readonly Lead360Role[];

  /**
   * NEXT-ACTION-OWNER-LIVE (S1-P8): the CURRENT open lead_actions assignment,
   * mirrored from the backend `Lead360Input.openActionOwner`. When present it
   * SUPERSEDES `assignedRole` for the displayed next-action owner (the live work
   * item is the authoritative owner, not the rule-table guess). Tri-state:
   *   - OMITTED: no open action -> fall back to the `assignedRole` label.
   *   - `{ assignedRole: <role> }`: an open action owned by that role.
   *   - `{ assignedRole: null }`: an open action with no role -> "Unassigned".
   * Display-only; never authorization. Stripped server-side for tiers without
   * the `nextActionOwner` section, so a non-leadership viewer never receives it.
   */
  readonly openActionOwner?: { readonly assignedRole: LeadActionOwnerRole | null };

  /**
   * OPTIONAL role-aware section manifest — the structural mirror of the backend
   * `Lead360Input.visibleSections` (lib/domain/lead/lead-360.ts,
   * S1-P4-ROLE-AWARE-LEAD360-VIEWS-01). When PRESENT, the backend produced this
   * record for a specific viewer tier and listed EXACTLY the sections that tier
   * may see; sections NOT listed had their field VALUES omitted server-side
   * (absent from the payload). When OMITTED (the owner endpoint, legacy / demo
   * producers, every existing fixture), consumers treat ALL sections as visible
   * — fully backward-compatible. Presentation only; never authorization.
   */
  readonly visibleSections?: readonly Lead360Section[];

  /**
   * OPTIONAL record-OWNERSHIP view (LEAD-ASSIGNMENT-OWNERSHIP-V1, V2-P0) -- the
   * structural mirror of the backend `Lead360Input.ownerAssignment`
   * (lib/domain/lead/lead-360.ts), kept in LOCKSTEP by review. Populated by the
   * additive route-level enrichment (DEFAULT-ON since TRAIN-B flag-sweep-2
   * retired the internal-surface env flag); it may still be ABSENT on older rows --
   * every existing fixture/producer that predates this field stays valid. When
   * ABSENT, consumers render NOTHING new (no owner chip, no POOL / escalation
   * badge, no owner controls); the My-leads / Unassigned filters treat an absent
   * value as a non-match (never a crash). PHI-free (see LeadOwnershipView).
   */
  readonly ownerAssignment?: LeadOwnershipView;

  /**
   * OPTIONAL per-record SLA/waiting state (MANAGER-OPS-COCKPIT-V2, rider R-29
   * absorption) -- the structural mirror of the backend
   * `Lead360Input.slaState` (lib/domain/lead/lead-360.ts), produced by the
   * record-wire SLA read seam reusing the ONE V4-P0 classifier. Tri-state by
   * optionality: OMITTED = the seam did not classify (fault fail-safe /
   * legacy producer) -> the band chip renders its explicit honest "not
   * available" state; `{ breaches: [] }` = classified CLEAR; breaches
   * carry the engine's controlled tokens only (class / severity / whole
   * waiting-age minutes). Display-only, never authorization.
   */
  readonly slaState?: Lead360SlaState;
}

/** One active breach on a record (controlled engine tokens only). */
export interface Lead360SlaBreach {
  /** The V4-P0 engine class vocabulary; validated at render time -- an
   *  unknown token renders through the count-only fallback, never raw. */
  readonly breachClass: string;
  readonly severity: 'neutral' | 'amber' | 'red';
  readonly ageMinutes: number;
  /**
   * LEAD-RECORD-POLISH-BATCH-V1 (CC-02): the SLA WINDOW this class breached
   * (whole minutes) -- the structural mirror of the backend
   * `Lead360SlaBreach.thresholdMinutes`, which is the ONE V4-P0 classifier's
   * own `thresholdMinutes` passed through the record seam. Lets the record
   * render "SLA 30m" / "Overdue by Xm" WITHOUT a second SLA computation.
   * OPTIONAL: absent on an older wire -> the consumer renders the waiting age
   * alone and claims no window (never a guessed threshold).
   */
  readonly thresholdMinutes?: number;
}

/** The record-wire SLA state (see `Lead360Input.slaState`). */
export interface Lead360SlaState {
  readonly breaches: readonly Lead360SlaBreach[];
}

// ─── Presentation output ─────────────────────────────────────────────

export type Lead360LifecycleCompleteness =
  | 'lead_only'
  | 'partial'
  | 'lead_plus_walkin'
  | 'complete'
  | 'unknown';

export type Lead360EventStreamStatus = 'not_wired' | 'available';

export interface Lead360View {
  readonly safeLeadId: string;
  /** Plain operator-visible lead name, or null when not captured. */
  readonly leadName: string | null;
  /** Normalized availability — absent input degrades to 'unknown'. */
  readonly phoneAvailability: Lead360PhoneAvailability;
  readonly clinicLabel: string;
  readonly sourceLabel: string;
  readonly utmSummary: string;
  readonly createdAtDisplay: string;
  readonly dataMode: Lead360DataMode;
  readonly dataModeLabel: string;
  readonly provenance: Lead360Provenance;
  /** `provenance.caveat`, surfaced at top level for convenience. */
  readonly caveat: string;
  readonly lifecycleCompleteness: Lead360LifecycleCompleteness;
  readonly missingHops: readonly string[];
  readonly events: readonly Lead360Event[];
  readonly eventStreamStatus: Lead360EventStreamStatus;
  readonly assignedRole: Lead360Role | 'unassigned';
  readonly visibleRoles: readonly Lead360Role[];
  /**
   * Role-aware section manifest, passed straight through from
   * `Lead360Input.visibleSections` (S1-P4-ROLE-AWARE-LEAD360-VIEWS-01). When
   * `undefined` (owner endpoint / legacy producers), consumers render ALL
   * sections; use `isLead360SectionVisible` to gate. Presentation only.
   */
  readonly visibleSections?: readonly Lead360Section[];
  /**
   * Display copy for WHO SHOULD own the next action (never a claim that an
   * action happened): a management-role label, or "Ownership unassigned" when
   * no rule assigns an owner. Display-only — never authorization.
   */
  readonly assignedRoleLabel: string;
  readonly nextActionLabel: string;
  // Safe boolean flags (definite links only — `probable`/`unknown` are not "has").
  readonly hasSource: boolean;
  readonly hasUtm: boolean;
  /**
   * Paid-click PRESENCE (yes/no) — passed straight through from
   * `Lead360Input.paidClickPresent`. A boolean only; the raw click-id value is
   * never carried on the contract, so it can never reach this view.
   */
  readonly hasPaidClick: boolean;
  readonly hasCallLink: boolean;
  readonly hasWalkinLink: boolean;
  readonly hasRevenueLink: boolean;
  readonly hasEventStream: boolean;
  /**
   * Record-OWNERSHIP view (LEAD-ASSIGNMENT-OWNERSHIP-V1), passed straight
   * through from `Lead360Input.ownerAssignment`. `undefined` when the
   * assignment flag was off for this record (no owner chip / badge / filter);
   * consumers gate every new render on its presence. Presentation only.
   */
  readonly ownerAssignment?: LeadOwnershipView;
}

// ─── Pure helpers ────────────────────────────────────────────────────

const NOT_CAPTURED = 'Not captured';

/** Roles eligible for lead visibility (also used to filter caller input). */
const VISIBILITY_ROLES: readonly Lead360Role[] = [
  'owner',
  'gm_agm',
  'regional_head',
  'coc',
];

const DATA_MODE_LABEL: Readonly<Record<Lead360DataMode, string>> = {
  real: 'Real data',
  demo: 'Demo data',
  test: 'Test data',
  manual: 'Manual entry',
  unknown: 'Unknown source',
};

/**
 * Display labels for the next-action owner (NEXT-ACTION-OWNERSHIP-READ-01).
 * Ownership is who SHOULD act next — the copy never implies an action was
 * taken. Role tokens only; never a person's name or identifier.
 */
const ASSIGNED_ROLE_LABEL: Readonly<Record<Lead360Role, string>> = {
  owner: 'Owner',
  gm_agm: 'GM/AGM',
  regional_head: 'Regional Head',
  coc: 'COC',
};

/** Honest copy when no rule assigns an owner — an owner is never invented. */
const UNASSIGNED_OWNERSHIP_LABEL = 'Ownership unassigned';

/**
 * Display labels for the LIVE next-action owner (NEXT-ACTION-OWNER-LIVE / S1-P8),
 * keyed by the work-routing vocabulary - INCLUDES 'receptionist' (the front
 * desk can own the next action), which the management-only ASSIGNED_ROLE_LABEL
 * above does not. Role tokens only; never a person's name.
 */
const LEAD_ACTION_OWNER_LABEL: Readonly<Record<LeadActionOwnerRole, string>> = {
  receptionist: 'Receptionist',
  coc: 'COC',
  regional_head: 'Regional Head',
  gm_agm: 'GM/AGM',
  owner: 'Owner',
  // LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (Refs #315): the blended-team call center
  // is the lawful lead owner; its stored assignment now renders truthfully.
  call_center_agent: 'Call center',
};

/** Honest copy when the live open action carries no assigned role. */
const LIVE_ACTION_UNASSIGNED_LABEL = 'Unassigned';

/** True only for a non-empty, non-whitespace string. Handles null/undefined. */
function isPresent(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * A lifecycle hop is CONFIRMED missing ONLY when its status is `no` or
 * `not_available`. `unknown` (and `probable`) are NOT confirmed missing —
 * reporting them as missing would turn uncertainty into false certainty.
 */
function isConfirmedMissingHop(status: Lead360HopStatus): boolean {
  return status === 'no' || status === 'not_available';
}

/**
 * True when a Lead 360 SECTION should render. The server-side role-aware read
 * path is the security boundary (restricted sections' field VALUES are already
 * absent from the payload); this is the honest UI companion so a hidden section
 * is not rendered as an empty "Not captured" state. BACKWARD-COMPATIBLE: when
 * `visibleSections` is `undefined` (owner endpoint / legacy / demo producers /
 * existing fixtures) EVERY section is visible, so today's surfaces render
 * byte-identically. Pure.
 */
export function isLead360SectionVisible(
  visibleSections: readonly Lead360Section[] | undefined,
  section: Lead360Section,
): boolean {
  return visibleSections === undefined || visibleSections.includes(section);
}

function utmSummaryOf(input: Lead360Input): string {
  const parts: string[] = [];
  if (isPresent(input.utmSource)) parts.push(`source=${input.utmSource.trim()}`);
  if (isPresent(input.utmMedium)) parts.push(`medium=${input.utmMedium.trim()}`);
  if (isPresent(input.utmCampaign)) parts.push(`campaign=${input.utmCampaign.trim()}`);
  if (isPresent(input.utmTerm)) parts.push(`term=${input.utmTerm.trim()}`);
  if (isPresent(input.utmContent)) parts.push(`content=${input.utmContent.trim()}`);
  return parts.length > 0 ? parts.join(', ') : NOT_CAPTURED;
}

// ─── Main projection ─────────────────────────────────────────────────

/**
 * Project a PHI-safe `Lead360Input` into a display-ready `Lead360View`. Pure:
 * no I/O, no mutation. Honesty is structural — missing/dark hops are reported as
 * `missingHops` and never fabricated, a probable walk-in stays probable, and an
 * empty event list DEFAULTS to `eventStreamStatus: 'not_wired'` unless the wired
 * read-model explicitly marks the (possibly empty) stream `'available'`.
 */
export function buildLead360View(input: Lead360Input): Lead360View {
  const hasSource =
    input.sourceCaptured === 'yes' || isPresent(input.sourceNormalized);
  const hasUtm =
    isPresent(input.utmSource) ||
    isPresent(input.utmMedium) ||
    isPresent(input.utmCampaign) ||
    isPresent(input.utmTerm) ||
    isPresent(input.utmContent);

  // Paid-click presence is a presence-only boolean produced by the backend
  // read-model (never the raw click-id). Absent input degrades to false.
  const hasPaidClick = input.paidClickPresent === true;

  // Definite-link flags only: `probable`/`unknown`/`not_available` are NOT "has".
  const hasCallLink = input.callLinked === 'yes';
  const hasWalkinLink = input.walkinLinked === 'yes';
  const hasRevenueLink = input.revenueLinked === 'yes';

  const events = input.events ?? [];
  // `hasEventStream` means real activity is PRESENT — a DISTINCT signal from "the
  // stream is wired". They diverge for a wired-but-empty read-model record.
  const hasEventStream = events.length > 0;
  // Precedence (exact): a wired read-model's explicit status wins, so a
  // wired-but-EMPTY stream stays 'available' (connected, no events yet) and never
  // collapses to 'not_wired'. Legacy/omitted producers fall back to "has events?".
  const eventStreamStatus: Lead360EventStreamStatus =
    input.eventStreamStatus ?? (hasEventStream ? 'available' : 'not_wired');

  // Missing hops — a hop is CONFIRMED missing ONLY when its status is `no` or
  // `not_available` (see isConfirmedMissingHop). `unknown`/`probable` are NOT
  // confirmed missing — surfacing them here would be false certainty; they are
  // reflected in lifecycleCompleteness / nextActionLabel instead. `source` is
  // additionally guarded by `!hasSource`, so a present source value never counts
  // as missing even if `sourceCaptured` is stale.
  const missingHops: string[] = [];
  if (!hasSource && isConfirmedMissingHop(input.sourceCaptured)) {
    missingHops.push('source_attribution');
  }
  if (isConfirmedMissingHop(input.callLinked)) missingHops.push('call_linkage');
  if (isConfirmedMissingHop(input.walkinLinked)) missingHops.push('walkin_linkage');
  if (isConfirmedMissingHop(input.revenueLinked)) missingHops.push('revenue_linkage');
  // The event stream is a "dark/missing" hop ONLY when it is not wired. A wired
  // stream with zero events is honestly connected (not missing), so it is never
  // listed here even though `hasEventStream` is false.
  if (eventStreamStatus !== 'available') missingHops.push('lead_event_stream');

  let lifecycleCompleteness: Lead360LifecycleCompleteness;
  if (input.leadCaptured !== 'yes') {
    lifecycleCompleteness = 'unknown';
  } else if (hasSource && hasWalkinLink && hasRevenueLink) {
    lifecycleCompleteness = 'complete';
  } else if (input.walkinLinked === 'yes' || input.walkinLinked === 'probable') {
    lifecycleCompleteness = 'lead_plus_walkin';
  } else if (hasSource) {
    lifecycleCompleteness = 'partial';
  } else {
    lifecycleCompleteness = 'lead_only';
  }

  // Role visibility — role-level only. Filter to allowed roles, dedupe, and ensure
  // the assigned role can always see its own lead. No per-user identity in v0.
  const assignedRole: Lead360Role | 'unassigned' =
    input.assignedRole != null && VISIBILITY_ROLES.includes(input.assignedRole)
      ? input.assignedRole
      : 'unassigned';

  const visibleRoles: Lead360Role[] = [];
  for (const r of input.visibleRoles ?? []) {
    if (VISIBILITY_ROLES.includes(r) && !visibleRoles.includes(r)) {
      visibleRoles.push(r);
    }
  }
  if (assignedRole !== 'unassigned' && !visibleRoles.includes(assignedRole)) {
    visibleRoles.push(assignedRole);
  }

  // Honest ownership copy. NEXT-ACTION-OWNER-LIVE (S1-P8): when the lead has a
  // live OPEN lead_actions row, its assignment is the authoritative next-action
  // owner and SUPERSEDES the rule-derived `assignedRole` label below - this is
  // why a lead routed to the receptionist no longer mislabels as "COC". A live
  // action with no role reads the honest "Unassigned"; with NO open action we
  // fall back to the existing rule-derived label (never invents an owner, never
  // implies an action was taken).
  const assignedRoleLabel = input.openActionOwner
    ? input.openActionOwner.assignedRole === null
      ? LIVE_ACTION_UNASSIGNED_LABEL
      : LEAD_ACTION_OWNER_LABEL[input.openActionOwner.assignedRole]
    : assignedRole === 'unassigned'
      ? UNASSIGNED_OWNERSHIP_LABEL
      : ASSIGNED_ROLE_LABEL[assignedRole];

  // Deterministic next action (the WHAT), prioritised by the earliest unmet
  // hop. LOCKSTEP NOTE (NEXT-ACTION-OWNERSHIP-READ-01;
  // LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1): this ladder computes the next-action
  // LABEL only. WHO owns the action is NO LONGER derived from these predicates:
  // the backend `deriveLeadOwnership` never guesses an owner, and the displayed
  // owner comes from the STORED `openActionOwner` (above) or renders UNASSIGNED.
  // Keep this ladder's predicates in step with the backend's retained lifecycle
  // ladder (both read the same hops); WHO and WHAT no longer share a value.
  let nextActionLabel: string;
  if (input.leadCaptured !== 'yes') {
    nextActionLabel = 'Confirm lead capture';
  } else if (!hasSource) {
    nextActionLabel = 'Tag lead source / UTM';
  } else if (input.walkinLinked === 'probable') {
    nextActionLabel = 'Confirm probable walk-in match';
  } else if (input.walkinLinked !== 'yes') {
    nextActionLabel = 'Follow up to convert to walk-in';
  } else if (input.revenueLinked !== 'yes') {
    nextActionLabel = 'Link revenue / outcome';
  } else {
    nextActionLabel = 'Lead fully linked — monitor';
  }

  const clinicLabel = isPresent(input.clinicLabel)
    ? input.clinicLabel.trim()
    : input.clinicId !== null
      ? `Clinic ${input.clinicId}`
      : 'Clinic not assigned';

  return {
    safeLeadId: input.safeLeadId,
    // CONTACT-REVEAL-AUDIT-01: plain operator identity + no-digit
    // availability. Honest defaults — null name is never invented and an
    // absent status reads 'unknown', never 'available'.
    leadName: isPresent(input.leadName) ? input.leadName.trim() : null,
    phoneAvailability: input.phoneAvailability ?? 'unknown',
    clinicLabel,
    sourceLabel: isPresent(input.sourceNormalized)
      ? input.sourceNormalized.trim()
      : NOT_CAPTURED,
    utmSummary: utmSummaryOf(input),
    createdAtDisplay: isPresent(input.createdAt)
      ? input.createdAt.trim()
      : NOT_CAPTURED,
    dataMode: input.dataMode,
    dataModeLabel: DATA_MODE_LABEL[input.dataMode],
    provenance: input.provenance,
    caveat: input.provenance.caveat,
    lifecycleCompleteness,
    missingHops,
    events,
    eventStreamStatus,
    assignedRole,
    visibleRoles,
    // Role-aware manifest passed straight through (undefined ⇒ all sections
    // visible). The server already stripped restricted-section field values.
    visibleSections: input.visibleSections,
    assignedRoleLabel,
    nextActionLabel,
    hasSource,
    hasUtm,
    hasPaidClick,
    hasCallLink,
    hasWalkinLink,
    hasRevenueLink,
    hasEventStream,
    // Record-ownership view passed straight through (undefined => the
    // assignment flag was off for this record; consumers render nothing new).
    ownerAssignment: input.ownerAssignment,
  };
}
